<template>
    <q-page padding>
        <div class="q-display-1 q-mb-md text-secondary">
            <q-icon name="far fa-tools" class="q-mb-sm" /> {{ $t('inputs') }}
        </div>

        <div class="row gutter-sm">
            <div class="col-12">
                <q-field :count="256">
                    <q-input v-model="title" :float-label="$t('title')" :error="$v.title.$error" @input="$v.title.$touch" />
                </q-field>
            </div>

            <div class="col-12">
                <q-field :count="1024">
                    <q-input v-model="description" type="textarea" :error="$v.description.$error" @input="$v.description.$touch" :float-label="$t('description')" />
                </q-field>
            </div>

            <div class="col-12">
                <q-input v-model="unitary_value" type="number" :float-label="$t('unitary_value')" prefix="$" @input="changeToDecimal" />
            </div>

            <div class="col-xs-12 col-sm-12 q-mt-sm">
                <q-checkbox v-model="all_areas" :val="true" :label="$t('select_all_areas')" @input="checkAllAreas()" />
                <q-select v-model="area" :options="areas" :float-label="$t('areas')" :error="$v.area.$error" @input="$v.area.$touch" multiple chips clearable @clear="deselectAreas"/>
            </div>

            <div class="col-12">
                <p class="caption">{{ $t('image') }}</p>
                <uploader content_type="images" @fileUploaded="imageUploaded" :multiple="false" :cropper="true" :aspect_ratio="1/1" :aspect_ratio_string="'1/1'" />

                <p v-if="image_url" class="q-mt-md file-url">
                    <q-icon name="fal fa-check" color="positive" />
                    <a :href="image_url" target="_blank"> {{ $truncateLink(image_url) }}</a>
                </p>
            </div>

            <div class="col-12">
                <p class="caption">{{ $t('status') }}</p>
                <q-btn-toggle v-model="status" :toggle-color="getColorStatus" :options="status_list" />
            </div>
        </div>

        <div class="row gutter-sm justify-end q-mt-lg">
            <div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
                <q-btn color="tertiary" class="full-width" :label="$t('back')" icon="far fa-chevron-left" @click="$router.push({ name: 'support_materials' })" />
            </div>

            <div class="col-xs-12 col-sm-3" :class="getOrder('save')">
                <q-btn color="secondary" class="full-width" :label="$t('save')" icon="far fa-save" @click="saveData()" />
            </div>
        </div>
    </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, minValue, maxLength } from 'vuelidate/lib/validators'
import Uploader from 'components/Uploader'

export default {
    i18nOptions: { namespaces: ['custom', 'common', 'presential', 'admin'] },

    data() {
        return {
            id: '',
            title: '',
            description: '',
            unitary_value: 0,
            image_url: '',
            all_areas: false,
            status: 1,
            status_list: [
                { label: this.$t('active'), value: 1 },
                { label: this.$t('inactive'), value: 2 }
            ],
            area: []
        }
    },

    components: {
        'uploader': Uploader,
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            areas: 'registration_lists/country_areas'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        getColorStatus() {
            if (this.status == 1) return 'positive'
            else return 'negative'
        },

        changeToDecimal() {
            if (this.unitary_value) this.unitary_value = this.unitary_value.toFixed(2)
        }
    },

    methods: {
        fetchData() {
            this.$q.loading.show()

            this.$axios.get(`/manager_materials/materials/${this.id}/`, this.getHeader)
            .then(response => {
                this.title = response.data.title
                this.description = response.data.description
                this.unitary_value = response.data.unitary_value
                this.status = response.data.status
                this.area = response.data.areas

                if (response.data.material_images.length > 0) {
                    this.image_url = response.data.material_images.find((obj) => { return obj.original }).image
                }
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
            })
        },

        saveData() {
            this.$v.$touch()

            if (!this.$v.$error) {
                this.$q.loading.show()

                let fd = new FormData()
                fd.append('title', this.title)
                fd.append('description', this.description)
                fd.append('unitary_value', this.unitary_value ? this.unitary_value : 0)
                fd.append('image_url', this.image_url)
                fd.append('status', this.status)

                this.area.forEach(area => {
                    fd.append('areas', area)
                })

                if (this.id != 0) {
                    this.$axios.put(`/manager_materials/materials/${this.id}/`, fd, this.getHeader)
                    .then(response => {
                        this.$q.notify({
                            message: this.$t('support_material_save_success'),
                            type: 'positive',
                            icon: 'fal fa-save'
                        })

                        this.$router.push({ name: 'support_materials' })
                    })
                    .catch(e => {
                        console.log(e)

                        this.$q.notify({
                            message: this.$t('support_material_save_error'),
                            type: 'negative',
                            icon: 'fal fa-ban'
                        })
                    })
                    .finally(() => {
                        this.$q.loading.hide()
                    })
                }
                else {
                    this.$axios.post(`/manager_materials/materials/`, fd, this.getHeader)
                    .then(response => {
                        this.$q.notify({
                            message: this.$t('support_material_save_success'),
                            type: 'positive',
                            icon: 'fal fa-save'
                        })

                        this.$router.push({ name: 'support_materials' })
                    })
                    .catch(e => {
                        console.log(e)

                        this.$q.notify({
                            message: this.$t('support_material_save_error'),
                            type: 'negative',
                            icon: 'fal fa-ban'
                        })
                    })
                    .finally(() => {
                        this.$q.loading.hide()
                    })
                }
            }
        },

        getOrder(name) {
            if (name == 'cancel') {
                if (this.$q.platform.is.mobile) return 'order-last'
                else return 'order-first'
            }
            else if (name == 'save') {
                if (this.$q.platform.is.mobile) return 'order-first'
                else return 'order-last'
            }
        },

        imageUploaded(url) {
            this.image_url = url
        },

        checkAllAreas() {
            if(this.all_areas) {
                this.area = this.areas.map(e => e.value)
            }
            else {
                this.area = []
            }
        },

        deselectAreas() {
            this.all_areas = false
            this.area = []
        }
    },

    validations() {
        return {
            title: { required, maxLength: maxLength(256) },
            description: { maxLength: maxLength(1024) },
            area: { required },
            unitary_value: {
                minValue: minValue(0)
            }
        }
    },

    created() {
        this.id = this.$route.params.id
        if (this.id != 0) this.fetchData()
    }
}
</script>

<style>

</style>
